import { useEffect, useRef, useState, useCallback } from 'react';

// Reveal-on-scroll: adds `.is-in` to every [data-lp-reveal] inside rootRef as it enters view.
// Honors an optional data-lp-delay (ms). Safety net reveals everything after 1.2s.
export const useReveal = (rootRef) => {
    useEffect(() => {
        const root = rootRef?.current;
        if (!root) return;
        const els = Array.from(root.querySelectorAll('[data-lp-reveal]'));
        const show = (el) => {
            if (el._revealed) return;
            el._revealed = true;
            const d = parseFloat(el.getAttribute('data-lp-delay') || '0');
            setTimeout(() => el.classList.add('is-in'), d);
        };
        let io;
        try {
            io = new IntersectionObserver((entries) => {
                entries.forEach(e => { if (e.isIntersecting) { show(e.target); io.unobserve(e.target); } });
            }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
            els.forEach(el => io.observe(el));
        } catch (e) {
            els.forEach(show);
        }
        const safety = setTimeout(() => els.forEach(show), 1200);
        return () => { if (io) io.disconnect(); clearTimeout(safety); };
    }, [rootRef]);
};

// Count-up when the element scrolls into view. Reads data-count / data-prefix / data-suffix.
export const useCountUp = (rootRef) => {
    useEffect(() => {
        const root = rootRef?.current;
        if (!root) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const els = Array.from(root.querySelectorAll('[data-count]'));
        const run = (el) => {
            if (el._counted) return;
            el._counted = true;
            const target = parseFloat(el.getAttribute('data-count'));
            const prefix = el.getAttribute('data-prefix') || '';
            const suffix = el.getAttribute('data-suffix') || '';
            if (reduce || isNaN(target)) { el.textContent = prefix + target + suffix; return; }
            const dur = 1300, start = performance.now();
            const step = (now) => {
                const t = Math.min((now - start) / dur, 1);
                el.textContent = prefix + Math.round(target * (1 - Math.pow(1 - t, 3))) + suffix;
                if (t < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        };
        let io;
        try {
            io = new IntersectionObserver((entries) => entries.forEach(e => { if (e.isIntersecting) run(e.target); }), { threshold: 0.4 });
            els.forEach(el => io.observe(el));
        } catch (e) { els.forEach(run); }
        const safety = setTimeout(() => els.forEach(run), 1600);
        return () => { if (io) io.disconnect(); clearTimeout(safety); };
    }, [rootRef]);
};

// Paged carousel: responsive visible count, arrows, dots, autoplay + pause on hover.
export const useCarousel = (total, { autoplayMs = 0 } = {}) => {
    const viewportRef = useRef(null);
    const trackRef = useRef(null);
    const pausedRef = useRef(false);
    const [page, setPage] = useState(0);
    const [pages, setPages] = useState(1);
    const pageRef = useRef(0);
    pageRef.current = page;

    const visibleFor = (w) => (w >= 760 ? 3 : w >= 480 ? 2 : 1);

    const layout = useCallback(() => {
        const vp = viewportRef.current, track = trackRef.current;
        if (!vp || !track || !total) return;
        const gap = 24;
        const v = visibleFor(vp.clientWidth);
        const cardW = Math.max(0, (vp.clientWidth - (v - 1) * gap) / v);
        Array.from(track.children).forEach(c => { c.style.width = cardW + 'px'; });
        const newPages = Math.max(1, Math.ceil(total / v));
        const p = Math.min(pageRef.current, newPages - 1);
        const maxOffset = Math.max(0, total - v);
        const offset = Math.min(p * v, maxOffset);
        track.style.transform = `translateX(${-(offset * (cardW + gap))}px)`;
        setPages(newPages);
        if (p !== pageRef.current) setPage(p);
    }, [total]);

    useEffect(() => {
        layout();
        const raf = requestAnimationFrame(layout);
        const t1 = setTimeout(layout, 350);
        const onResize = () => layout();
        window.addEventListener('resize', onResize, { passive: true });
        return () => { cancelAnimationFrame(raf); clearTimeout(t1); window.removeEventListener('resize', onResize); };
    }, [layout]);

    useEffect(() => { layout(); }, [page, layout]);

    const goTo = useCallback((p) => setPage(p), []);
    const next = useCallback(() => setPage(p => (p + 1) % Math.max(1, pages)), [pages]);
    const prev = useCallback(() => setPage(p => (p - 1 + Math.max(1, pages)) % Math.max(1, pages)), [pages]);

    useEffect(() => {
        if (!autoplayMs) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) return;
        const id = setInterval(() => { if (!pausedRef.current) next(); }, autoplayMs);
        return () => clearInterval(id);
    }, [autoplayMs, next]);

    const pause = () => { pausedRef.current = true; };
    const resume = () => { pausedRef.current = false; };

    return { viewportRef, trackRef, page, pages, next, prev, goTo, pause, resume };
};

// Single-slide-per-view slider: full-width slides, translateX(-index*100%), arrows + dots + autoplay.
export const useSlideIndex = (total, { autoplayMs = 0 } = {}) => {
    const pausedRef = useRef(false);
    const [index, setIndex] = useState(0);

    const goTo = useCallback((i) => setIndex(Math.max(0, Math.min(i, total - 1))), [total]);
    const next = useCallback(() => setIndex(i => (i + 1) % total), [total]);
    const prev = useCallback(() => setIndex(i => (i - 1 + total) % total), [total]);

    useEffect(() => {
        if (!autoplayMs) return;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) return;
        const id = setInterval(() => { if (!pausedRef.current) next(); }, autoplayMs);
        return () => clearInterval(id);
    }, [autoplayMs, next]);

    const pause = () => { pausedRef.current = true; };
    const resume = () => { pausedRef.current = false; };

    return { index, total, next, prev, goTo, pause, resume };
};
